import { ReactElement, useState } from "react";
import { Alert, TextField, Button, Divider } from "@mui/material";
import MethodInfo from "./FormInfo";

export interface FilterCellsGenesFormProps {
  onClose?: () => void;
  onSubmit?: (data: FilterCellsGenesData) => void;
  onBack?: () => void;
  onContinue?: () => void;
  className?: string;
}

export interface FilterCellsGenesData {
  minGenes: number;
  maxGenes: number;
  minCells: number;
  maxMitoPercent: number;
}

const FilterCellsGenesForm = ({
  onClose,
  onSubmit,
  onBack,
  onContinue,
  className = "",
}: FilterCellsGenesFormProps): ReactElement => {
  const [formData, setFormData] = useState<Record<keyof FilterCellsGenesData, string>>({
    minGenes: "200",
    maxGenes: "2500",
    minCells: "3",
    maxMitoPercent: "10",
  });
  const [formError, setFormError] = useState("");

  const handleInputChange = (field: keyof FilterCellsGenesData, value: string) => {
    setFormError("");
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const parsed: FilterCellsGenesData = {
      minGenes: Number(formData.minGenes),
      maxGenes: Number(formData.maxGenes),
      minCells: Number(formData.minCells),
      maxMitoPercent: Number(formData.maxMitoPercent),
    };

    if (Object.values(formData).some((value) => value.trim() === "")) {
      setFormError("Enter a value for every filtering threshold.");
      return;
    }
    if (parsed.maxGenes <= parsed.minGenes) {
      setFormError("Max Genes must be greater than Min Genes.");
      return;
    }
    if (parsed.maxMitoPercent < 0 || parsed.maxMitoPercent > 100) {
      setFormError("Mitochondrial content must be between 0% and 100%.");
      return;
    }

    onSubmit?.(parsed);
  };

  return (
    <div
      className={`p-6 h-full flex flex-col justify-between ${className}`}
      style={{
        backgroundColor: "#F5F5F5", // tokens.colors.gray[100]
      }}
    >
      {/* Description */}
      <MethodInfo
        title="Filtering rules"
        message="Cells must satisfy all three rules: detected genes at or above Min Genes, at or below Max Genes, and mitochondrial reads at or below Max Mitochondrial Content. Genes detected in fewer than Min Cells are then removed."
      />

      <Divider className="my-4" />

      {formError && (
        <Alert severity="warning" className="mb-4">
          {formError}
        </Alert>
      )}

      {/* Form Fields */}
      <div className="space-y-6 flex-1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TextField
            label="Min Genes (per Cell)"
            type="number"
            value={formData.minGenes}
            onChange={(e) => handleInputChange("minGenes", e.target.value)}
            inputProps={{
              min: 0,
              step: 1,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                "& fieldset": {
                  borderColor: "#d1d5db",
                },
                "&:hover fieldset": {
                  borderColor: "#19C1DB",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#19C1DB",
                },
              },
            }}
          />

          <TextField
            label="Max Genes (per Cell)"
            type="number"
            value={formData.maxGenes}
            onChange={(e) => handleInputChange("maxGenes", e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                "& fieldset": { borderColor: "#d1d5db" },
                "&:hover fieldset": { borderColor: "#19C1DB" },
                "&.Mui-focused fieldset": { borderColor: "#19C1DB" },
              },
            }}
          />

          <TextField
            label="Min Cells (per Gene)"
            type="number"
            value={formData.minCells}
            onChange={(e) => handleInputChange("minCells", e.target.value)}
            inputProps={{
              min: 0,
              step: 1,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                "& fieldset": {
                  borderColor: "#d1d5db",
                },
                "&:hover fieldset": {
                  borderColor: "#19C1DB",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#19C1DB",
                },
              },
            }}
          />

          <TextField
            label="Max Mitochondrial Content (%)"
            type="number"
            value={formData.maxMitoPercent}
            onChange={(e) => handleInputChange("maxMitoPercent", e.target.value)}
            inputProps={{ min: 0, max: 100, step: 0.5 }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                "& fieldset": { borderColor: "#d1d5db" },
                "&:hover fieldset": { borderColor: "#19C1DB" },
                "&.Mui-focused fieldset": { borderColor: "#19C1DB" },
              },
            }}
          />
        </div>
      </div>

      {/* Action Buttons - Back (left) and Submit (right) */}
      <div className="flex justify-between mt-6 sticky bottom-0 bg-[#F5F5F5] pt-3">
        {onBack && (
          <Button
            variant="outlined"
            onClick={onBack}
            className="border-[#19C1DB] text-[#19C1DB] hover:bg-[#19C1DB] hover:text-white"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 1.5,
            }}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          className="bg-[#19C1DB] hover:bg-[#17B1CB] text-white"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            py: 1.5,
          }}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default FilterCellsGenesForm;
